/**
 * The half of the accent that touches the page (`accent.ts` is the rest),
 * all of it the shell's (`packages/shell/INTEGRATION.md` 4.5,
 * `docs/decisions/0056`, `0057`):
 *
 * - **at runtime**, resolve the chosen colour for the base on show and set
 *   the seven identity tokens inline on `<html>` with `applyAccent`, where
 *   they beat `theme.css` and `app.css` alike;
 * - **for the next load's first frame**, cache the tokens for both bases with
 *   `storeAccentCache` under {@link ACCENT_CACHE_KEY}; the shell's
 *   `accent-boot.js`, loaded blocking in `index.html` after
 *   `appearance-boot.js` with the same key, sets the set for the appearance
 *   just stamped before the bundle runs.
 *
 * The key is the one Interlock's own boot script used before shell 1.4.0
 * (`interlock:accent`). Its old records carry no version, so the shell's
 * boot script passes over them, and the stylesheet's blue shows for one
 * frame on the first load after the update; the app then rewrites the record
 * in the shell's shape. The choice itself lives in the settings blob and is
 * never lost.
 */
import { applyAccent, storeAccentCache } from '@artibens/app-shell';
import type { AccentBase, AccentTokens } from '@artibens/app-shell/accent';

import { accentTokens, parseAccent } from './accent.js';

/** Where the resolved tokens are cached for the shell's `accent-boot.js` (`index.html`'s `data-key`). */
export const ACCENT_CACHE_KEY = 'interlock:accent';

/**
 * Shows `hex` on `base`. Called on start-up, on every change of the accent or
 * the appearance, after a sync, and on every move of the picker's sliders (it
 * is cheap enough). Returns the tokens it set.
 */
export function showAccent(
  hex: string,
  base: AccentBase,
  root: HTMLElement = document.documentElement,
): AccentTokens {
  const tokens = accentTokens(hex, base);
  applyAccent(root, tokens);
  return tokens;
}

/**
 * Caches `hex`'s tokens on both bases for the boot script. A refused write
 * is not an error: the next load paints the stylesheet's blue for a frame.
 */
export function rememberAccent(hex: string): void {
  const colour = parseAccent(hex);
  storeAccentCache(ACCENT_CACHE_KEY, colour, {
    dark: accentTokens(colour, 'dark'),
    light: accentTokens(colour, 'light'),
  });
}
